// ─────────────────────────────────────────────────────────────────────────────
// Innovación LATAM — landing de campaña personalizada (/app/innovacion).
//
// Paridad con `CampaignCustomPage.tsx` del demo (banner de campaña, fila de KPIs, CTA de nominar
// con panel embebido, fundamentales de la campaña, documentos y reconocimientos recientes),
// adaptada a los patrones de este repo: i18n, hooks de `@/api/recognition`, clases `.inn-*` propias
// (H-85/H-86: el demo es 100% inline, `.inn-page`/`.inn-hero` ya no reutilizan `.bj-hero`).
//
// DIFERENCIA DELIBERADA CON EL DEMO: el demo trae TODO hardcodeado (racha "6 semanas",
// "47 nominaciones", "84 días", nombres de personas inventados, 2 Fundamentales fijos de la
// campaña). Aquí cada número sale del backend. `CampaignDto` no expone una lista de Fundamentales
// propios de la campaña (no existe ese campo en el contrato real), así que la sección "Fundamentales
// de esta campaña" muestra el catálogo REAL completo en vez de inventar 2 — mismo criterio de
// honestidad que el resto del proyecto (ver H-85..H-90 en HALLAZGOS-PARIDAD-DEMO.md, sección F7).
// Si el cliente no tiene una campaña Personalizada activa, la vista muestra un estado vacío
// explícito — nunca datos ficticios (ese fue justo el problema que disparó esta corrección).
//
// Gating: solo AdminGlobal, igual que el demo, donde la entrada solo la ve `admin_central`.
// La fuente de verdad del gating es RECOGNITION_ACCESS.innovacion en UserContext.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCampaigns, useRecognitionCampaignLive, useRecognitions, useRecognitionCatalog,
  getCampaignName, getFundamentalName, getFundamentalColor, pickLocalized,
} from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import { InitialsAvatar } from '@/components/common';
import { nombreDelEmisor } from './postitShared';
import { useEffectiveRole } from '@/auth/RoleGuard';
import { canAccessRecognitionEntry } from '@/contexts/UserContext';
import { streakUnitCopy } from '@/utils/streakUnit';
import { RecognitionShell, RecognitionHeader, StateBlock } from './components';
import { CampaignDocs } from './CampaignDocs';
import { BrillamosStars } from './BrillamosStars';
import { StreakWidget } from './StreakWidget';
import { useVisibleStreak } from './useVisibleStreak';
import { ReconocerPanel } from './ReconocerPanel';
import { daysLeft, formatRelative } from './brillamosShared';

function formatDateRange(campaign: CampaignDto, lang: string): string {
  const fmt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const from = fmt(campaign.fechaInicio);
  const to = fmt(campaign.fechaFin);
  if (from && to) return `${from} — ${to}`;
  return from ?? to ?? '';
}

/** Tarjeta de KPI. `value === null` = el backend no expuso el dato (no se inventa un número). */
function KpiCard({
  label, value, hint, accent,
}: Readonly<{ label: string; value: string | null; hint?: string; accent: 'racha' | 'nominaciones' | 'dias' }>) {
  return (
    <div className={`inn-kpi inn-kpi--${accent}`}>
      <div className="inn-kpi-label">{label}</div>
      <div className="inn-kpi-value">{value ?? '—'}</div>
      {hint && <div className="inn-kpi-hint">{hint}</div>}
    </div>
  );
}

function InnovacionLanding({ campaign, lang }: Readonly<{ campaign: CampaignDto; lang: string }>) {
  const { t } = useTranslation('recognition');
  const [showNominar, setShowNominar] = useState(false);
  const active = campaign.estado === 'Activa';
  const left = daysLeft(campaign);
  const description = pickLocalized(campaign.descripcion, lang);

  // KPIs en vivo. `suprimido` = k-anonimato del backend: hay datos pero no se pueden mostrar.
  const live = useRecognitionCampaignLive({ campaignId: campaign.id, language: lang }, true);
  const suppressed = live.data?.suprimido === true;

  // RF-043 — apagada la racha en Gestión, `streak` es undefined y con él desaparecen la tarjeta KPI
  // de racha y el widget del pie: la landing de campaña deja de mencionarla, que es literalmente lo
  // que pide el RF ("que no aparezca en campaña").
  // BUG (racha inconsistente, 2026-08-23) — esta landing es SIEMPRE de una campaña Personalizada
  // (línea 199 más abajo), y `RecognitionProgramResolver.ProgramaDeCampana` (backend) bucketea TODO
  // lo que no es Fundamentales bajo el programa 'Brillamos' — no existe un tercer programa
  // 'Personalizada' para la racha. Pedirla sin `programa` (Fundamentales por defecto) mostraba una
  // racha distinta a la que el mismo usuario ve en Brillamos/Perfil.
  const streak = useVisibleStreak('Brillamos');
  const catalog = useRecognitionCatalog();

  const feed = useRecognitions({ campaignId: campaign.id, page: 1, pageSize: 5 });
  const recent = feed.data?.items ?? [];

  return (
    <div className="inn-page">
      <BrillamosStars />

      {/* Banner — H-85: gradiente literal del demo, ya no reutiliza .bj-hero (H-86). */}
      <div className="inn-hero">
        <span className="inn-hero-emoji" aria-hidden="true">🚀</span>
        <h1 className="inn-hero-title">{getCampaignName(campaign, lang)}</h1>
        <p className="inn-hero-dates">{description || formatDateRange(campaign, lang)}</p>
      </div>

      {/* Estado de campaña — solo integrado (el demo no lo tiene en esta landing); se conserva
          restilado con el lenguaje visual del demo en vez de retirarse (regla 6, punto 8). */}
      <div className="inn-status-row">
        {active
          ? <span className="bj-badge-on"><span className="bj-pulse" /> {t('home.active')}</span>
          : <span className="bj-badge-off">{t('home.ended')}</span>}
        {left != null && active && (
          <span className="bj-badge-dur"><i className="fas fa-clock" /> {t('home.daysLeft', { count: left })}</span>
        )}
        <span className="bj-badge-custom"><i className="fas fa-bullhorn" /> {t('campaignType.personalizada')}</span>
      </div>

      {/* KPIs — H-87: Racha/Nominaciones/Días restantes, color propio por tarjeta.
          Sin datos del backend se muestran guiones, no cifras inventadas. */}
      <div className="inn-kpis">
        {/* RF-043 — La tarjeta de racha se monta SOLO con datos de racha visibles, nunca con el "—"
            de relleno: un guión bajo el rótulo "Racha" sigue siendo mencionarla, y con `retry: 0`
            (ver `useMyStreak`) un fallo de esa consulta la dejaría ahí para siempre. Quitarla no
            cuesta nada de maquetado: `.inn-kpis` es flex con `flex: 1 1 180px`, así que las
            restantes se reparten el ancho — no hay columna fija que quede hueca. */}
        {streak && (
          <KpiCard
            accent="racha"
            label={t('innovacion.kpiStreak')}
            value={String(streak.semanasActuales)}
            // RF-037: la unidad sigue a la periodicidad, y el texto ya no afirma "mín. 1
            // reco/semana": el mínimo es configurable (1–20, global o por campaña) y esa frase era
            // falsa en cuanto alguien lo cambiara, que es justo el objeto del RF.
            hint={t(streakUnitCopy(streak.periodicidad).kpiHint, { record: streak.record })}
          />
        )}
        <KpiCard
          accent="nominaciones"
          label={t('innovacion.kpiNominations')}
          value={live.data && !suppressed ? String(live.data.nominaciones) : null}
          hint={
            live.data && !suppressed
              ? t('innovacion.kpiNominationsHint2', { count: live.data.nominadosUnicos })
              : t('innovacion.kpiNominationsHint')
          }
        />
        <KpiCard
          accent="dias"
          label={t('innovacion.kpiDaysLeft')}
          value={left != null ? String(left) : null}
          hint={formatDateRange(campaign, lang)}
        />
      </div>
      {suppressed && (
        <p className="rp-note"><i className="fas fa-user-shield" aria-hidden="true" /> {t('innovacion.suppressed')}</p>
      )}
      {live.isError && (
        <p className="rp-note"><i className="fas fa-circle-info" aria-hidden="true" /> {t('innovacion.kpisUnavailable')}</p>
      )}

      {/* Nominar — H-89: el demo expande el panel de reconocimiento EN LA MISMA PANTALLA. Se
          conserva el link a /app/nominar como alternativa secundaria (no se retira, punto 6). */}
      {active && (
        <button type="button" className="inn-nominar-cta" onClick={() => setShowNominar((v) => !v)}>
          <i className="fas fa-paper-plane" aria-hidden="true" /> {t('home.nominateNow')}
        </button>
      )}
      {showNominar && (
        <div className="inn-nominar-panel">
          <ReconocerPanel />
        </div>
      )}
      <p className="inn-nominar-alt">
        <Link to="/app/nominar" className="inn-nominar-alt-link">
          <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" /> {t('innovacion.nominateSecondary')}
        </Link>
      </p>

      {/* Fundamentales de esta campaña — H-88, ausente por completo antes de esta fase. */}
      {catalog.data && catalog.data.length > 0 && (
        <div className="inn-funds">
          <h3 className="inn-section-label">{t('innovacion.fundamentalsTitle')}</h3>
          <div className="inn-fund-chips">
            {catalog.data.map((f, i) => {
              const color = getFundamentalColor(f, i);
              return (
                <span key={f.id} className="inn-fund-chip" style={{ borderColor: `${color}30` } as React.CSSProperties}>
                  <span className="inn-fund-dot" style={{ background: color }} />
                  <span className="inn-fund-name" style={{ color }}>{getFundamentalName(f, lang)}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* R8b (Tarea 15) — `programa={campaign.tipo}` ('Personalizada'): hoy el wizard de "Campañas
          por OU" solo permite crear excepciones de programa Fundamentales/Brillamos, así que este
          nivel intermedio nunca encuentra coincidencia aquí — se deja declarado por si el wizard
          llega a soportar Personalizada más adelante, sin necesitar tocar esta pantalla otra vez.
          `isPersonalizada` (2026-08-18) apaga el nivel 4 (corporativos): esta pantalla es SIEMPRE
          de campaña Personalizada, así que nunca debe caer al repositorio corporativo genérico. */}
      <CampaignDocs
        campaignId={campaign.id}
        programa={campaign.tipo}
        isPersonalizada={campaign.tipo === 'Personalizada'}
      />

      {/* Racha/insignias — funcionalidad solo del integrado (el demo no la tiene en esta
          landing); se conserva restilada en su propia sección en vez de retirarse (punto 8). */}
      {/* RF-043 — el envoltorio también se gatea: `.inn-streak-section` lleva `margin-bottom: 20px`,
          así que dejarlo montado con el widget en null deja un hueco donde estaba la racha. */}
      {streak && (
        <div className="inn-streak-section">
          <StreakWidget programa="Brillamos" />
        </div>
      )}

      <section className="bj-feed-section">
        <h3 className="inn-section-label">{t('innovacion.recentTitle')}</h3>
        <StateBlock
          isLoading={feed.isLoading}
          isError={feed.isError}
          error={feed.error}
          isEmpty={recent.length === 0}
          emptyText={t('innovacion.recentEmpty')}
          onRetry={() => { feed.refetch(); }}
        >
          {/* H-90: fila horizontal con avatar circular de iniciales de 36px, no grid de .bj-card. */}
          <div className="inn-feed-list">
            {recent.map((r) => {
              const emisor = nombreDelEmisor(r, t);
              return (
                <div key={r.id} className="inn-feed-row">
                  <InitialsAvatar
                    name={r.emisorNombre ?? ''}
                    baseClass="inn-feed-avatar"
                    seed={r.emisorId ?? r.id}
                  />
                  <div className="inn-feed-body">
                    <div className="inn-feed-names">{emisor} → {r.receptorNombres.join(', ')}</div>
                    {r.mensaje && <div className="inn-feed-fund">{r.mensaje}</div>}
                  </div>
                  <span className="inn-feed-time">{formatRelative(r.fecha, t)}</span>
                </div>
              );
            })}
          </div>
        </StateBlock>
      </section>
    </div>
  );
}

function InnovacionContent() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const role = useEffectiveRole();

  // Gating de rol — misma regla que la entrada del sidebar. El backend revalida de todos modos.
  const allowed = canAccessRecognitionEntry('innovacion', role);

  // La campaña "Innovación LATAM" del demo es, en este producto, una campaña de tipo
  // `Personalizada` administrada desde Admin → Campañas. Se resuelve la activa; no se codifica
  // ningún id ni nombre, para que funcione con la campaña real que cargue el cliente.
  const campaigns = useCampaigns('Activa', 1, 50, allowed);
  const campaign = (campaigns.data?.items ?? []).find((c) => c.tipo === 'Personalizada') ?? null;

  if (!allowed) {
    return (
      <>
        <RecognitionHeader title={t('nav.innovation')} />
        <p className="adm-denied" role="alert">
          <i className="fas fa-lock" aria-hidden="true" /> {t('admin.denied')}
        </p>
      </>
    );
  }

  return (
    <StateBlock
      isLoading={campaigns.isLoading}
      isError={campaigns.isError}
      error={campaigns.error}
      isEmpty={campaign === null}
      emptyText={t('innovacion.noCampaign')}
      onRetry={() => { campaigns.refetch(); }}
    >
      {campaign && <InnovacionLanding campaign={campaign} lang={lang} />}
    </StateBlock>
  );
}

export function InnovacionView() {
  return (
    <RecognitionShell>
      <InnovacionContent />
    </RecognitionShell>
  );
}

export default InnovacionView;
